import { useMutation } from '@tanstack/react-query';
import { hasPlatformAccess, validateMFA } from '@/features/auth/services/auth';
import type {
  MFAValidateRequest,
  MFAValidateResponse,
} from '@/features/auth/types/auth.types';
import { useAuthStore } from '@/features/auth/store/auth';
import i18n from '@/shared/i18n';

interface UseMFAValidateOptions {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
  onAccessNotGranted?: (email: string) => void;
}

/**
 * Hook for MFA code validation
 * Handles MFA validation and navigation on success
 *
 * @param options - Callbacks for success and error handling
 * @example
 * ```tsx
 * const { mutate: validate, isPending } = useMFAValidate({
 *   onSuccess: () => {
 *     navigate('/');
 *   },
 *   onError: (error) => {
 *     console.error('Invalid code:', error);
 *   }
 * });
 *
 * validate({ email: 'user@example.com', code: '123456' });
 * ```
 */
export function useMFAValidate(options?: UseMFAValidateOptions) {
  const { setError, mfaFulfilled, username } = useAuthStore();

  return useMutation<MFAValidateResponse, Error, MFAValidateRequest>({
    mutationFn: (payload) => validateMFA(payload),
    onSuccess: async (data) => {
      if (!(await hasPlatformAccess(data.accessToken))) {
        setError(null);
        options?.onAccessNotGranted?.(username);
        return;
      }

      mfaFulfilled({
        accessToken: data.accessToken,
        expiresIn: data.expiresIn,
        tokenType: data.tokenType,
        scope: data.scope,
      });
      options?.onSuccess?.();
      return;
    },
    onError: (error) => {
      setError(i18n.t('Incorrect code!'));

      options?.onError?.(error);
    },
  });
}
